import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import MigrationCrunchFooter from '../components/MigrationCrunchFooter';
import MigrationConciergeFloat from '../components/MigrationConciergeFloat';
import {
  Database,
  Sparkle,
  CheckCircle,
  WarningCircle,
  ArrowRight,
  Scales,
} from '@phosphor-icons/react';
import { getCompetitor, listCompetitorSlugs } from '../data/competitors';

/**
 * /migrate/vs/:competitor — dedicated SEO landing page for each competitor.
 *
 * Renders a respectful, structured comparison sourced from competitors.js.
 * Mirrors the same nav + footer pattern used on MigrationComparePage so the
 * full set of /migrate surfaces feels like one product, not stitched
 * micro-sites.
 *
 * Unknown slugs render a noindex 404-style fallback (same pattern as
 * MigrationPairPage.js for unknown migration pairs).
 */
export default function CompetitorComparePage({ slugOverride } = {}) {
  const { competitor } = useParams();
  // slugOverride is used by the short-URL alias routes (/vs-import2 etc.)
  // where the slug isn't in the URL path. Falls back to the :competitor
  // route param for the canonical /migrate/vs/:competitor route.
  const data = getCompetitor(slugOverride || competitor);

  // Hooks must run unconditionally — keep SEO effect above any early return.
  useEffect(() => {
    if (!data) return undefined;
    const prevTitle = document.title;
    document.title = data.title;
    const upsertMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      let created = false;
      let prev = null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created = true;
      } else {
        prev = el.getAttribute('content');
      }
      el.setAttribute('content', content);
      return () => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute('content', prev);
      };
    };
    const url = `${window.location.origin}/migrate/vs/${data.slug}`;
    const img = `${window.location.origin}/og-default.svg`;
    const restorers = [
      upsertMeta('name', 'description', data.metaDescription),
      upsertMeta('property', 'og:title', data.title),
      upsertMeta('property', 'og:description', data.metaDescription),
      upsertMeta('property', 'og:url', url),
      upsertMeta('property', 'og:type', 'website'),
      upsertMeta('property', 'og:site_name', 'MigrationCrunch'),
      upsertMeta('property', 'og:image', img),
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      upsertMeta('name', 'twitter:title', data.title),
      upsertMeta('name', 'twitter:description', data.metaDescription),
      upsertMeta('name', 'twitter:image', img),
    ];
    return () => {
      document.title = prevTitle;
      restorers.forEach((fn) => fn());
    };
  }, [data]);

  if (!data) {
    // Redirect bad slugs back to the general compare page. SPA-level fallback.
    return <Navigate to="/migrate/compare" replace />;
  }

  const otherCompetitors = listCompetitorSlugs().filter((s) => s !== data.slug);

  return (
    <div
      className="min-h-screen bg-[#0F0F1A] text-white"
      data-testid={`mc-vs-${data.slug}-page`}
    >
      {/* Shared MigrationCrunch nav */}
      <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0F0F1A]/95 backdrop-blur-sm z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link
            to="/migrate"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="mc-nav-logo"
          >
            <Database size={28} weight="fill" className="text-[#F39C12]" />
            <span className="text-xl font-black">
              Migration<span className="text-[#F39C12]">Crunch</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link
              to="/migrate#fit-check"
              className="text-white/70 hover:text-amber-300 transition-colors"
              data-testid="mc-nav-fit"
            >
              Fit check
            </Link>
            <Link
              to="/migrate/compare"
              className="text-white/70 hover:text-amber-300 transition-colors"
              data-testid="mc-nav-compare"
            >
              Compare vendors
            </Link>
            <Link
              to="/migrate#pricing"
              className="text-white/70 hover:text-amber-300 transition-colors"
              data-testid="mc-nav-pricing"
            >
              Pricing
            </Link>
            <a
              href="/"
              className="text-sm text-white/70 hover:text-white border border-white/20 hover:border-white/40 rounded px-3 py-1.5 transition-colors"
              data-testid="mc-nav-try-crm"
            >
              Try SalesCrunchCRM →
            </a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="px-6 py-16 md:py-20 border-b border-white/10">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-amber-300 uppercase mb-3">
            <Scales size={12} weight="fill" /> Head-to-head · February 2026
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">
            MigrationCrunch{' '}
            <span className="text-white/40 font-light">vs</span>{' '}
            <span className="text-amber-400">{data.h1Their}</span>
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto leading-relaxed">
            {data.tagline} Here is the honest, tier-by-tier comparison —
            including the scenarios where {data.name} is the better fit.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/migrate#fit-check"
              className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-md transition-colors inline-flex items-center justify-center gap-2"
              data-testid={`mc-vs-${data.slug}-fit-cta`}
            >
              Take the 30-second fit check{' '}
              <ArrowRight size={16} weight="bold" />
            </a>
            <Link
              to="/migrate/compare"
              className="px-6 py-3 border border-white/20 hover:border-white/40 text-white font-medium rounded-md transition-colors inline-flex items-center justify-center gap-2"
              data-testid={`mc-vs-${data.slug}-compare-all-cta`}
            >
              Compare all vendors
            </Link>
          </div>
        </div>
      </section>

      {/* RECORD-COUNTING EXPLAINER — Lindy's plain-English reveal of how
          the competitor's pricing model *actually* works. Placed right
          after the hero because this is the single insight that reframes
          the whole comparison. If a shopper reads only ONE section, this
          is the one that changes their mind. */}
      {data.recordCountingExplainer && (
        <section className="px-6 py-14 border-b border-white/10 bg-amber-500/5">
          <div className="max-w-4xl mx-auto">
            <div className="text-xs font-black uppercase tracking-widest text-amber-400 mb-3">
              🔢 The pricing model you need to understand
            </div>
            <h2 className="text-2xl md:text-3xl font-black mb-4">
              {data.recordCountingExplainer.headline}
            </h2>
            <p className="text-base text-white/80 leading-relaxed mb-6">
              {data.recordCountingExplainer.body}
            </p>
            {/* Warning callout */}
            <div className="bg-amber-500/10 border-l-4 border-amber-400 p-4 mb-8 text-sm text-amber-100">
              <strong className="text-amber-300">⚠️ Heads up:</strong>{' '}
              {data.recordCountingExplainer.warning}
            </div>
            {/* Record-price table */}
            <div className="bg-white/[0.04] border border-white/10 rounded-lg overflow-hidden">
              <div className="grid grid-cols-3 gap-0 text-[10px] uppercase tracking-widest font-black text-white/60 border-b border-white/10">
                <div className="px-4 py-3">Your database</div>
                <div className="px-4 py-3 border-l border-white/10">{data.name} &ldquo;records&rdquo;</div>
                <div className="px-4 py-3 border-l border-white/10">{data.name} price</div>
              </div>
              {data.recordCountingExplainer.recordExamples.map((row, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-3 gap-0 text-sm border-b border-white/5 last:border-b-0 ${row.jump ? 'bg-amber-500/[0.08]' : ''}`}
                  data-testid={`mc-vs-${data.slug}-record-row-${i}`}
                >
                  <div className="px-4 py-3 text-white/85">{row.desc}</div>
                  <div className="px-4 py-3 border-l border-white/5 text-white/70">{row.records}</div>
                  <div className={`px-4 py-3 border-l border-white/5 font-bold ${row.jump ? 'text-amber-300' : 'text-white'}`}>
                    {row.theirPrice}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TIME TO COMPLETE — the other headline number after price. Placed
          immediately after the pricing explainer because the "minutes vs
          days" contrast is our sharpest differentiator per Lindy's read. */}
      {data.timeToComplete && (
        <section className="px-6 py-14 border-b border-white/10">
          <div className="max-w-4xl mx-auto">
            <div className="text-xs font-black uppercase tracking-widest text-emerald-300 mb-3">
              ⏱️ Time to complete
            </div>
            <h2 className="text-2xl md:text-3xl font-black mb-6">
              How long does {data.name} take?
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-2">
                  MigrationCrunch
                </div>
                <div className="text-2xl font-black text-white">
                  {data.timeToComplete.ours}
                </div>
              </div>
              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-2">
                  {data.name}
                </div>
                <div className="text-2xl font-black text-white/85">
                  {data.timeToComplete.theirs}
                </div>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              {data.timeToComplete.note}
            </p>
          </div>
        </section>
      )}

      {/* THEIR STRENGTHS — respectful tone, said first */}
      <section className="px-6 py-14 border-b border-white/10 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black mb-2">
            {data.strengthsHeadline}
          </h2>
          <p className="text-sm text-white/55 mb-8">
            Said plainly, in their voice. If these are the things you most
            need, pick {data.name} — no hard feelings.
          </p>
          <ul className="grid md:grid-cols-2 gap-3">
            {data.strengths.map((s, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white/[0.03] border border-white/10 rounded-md p-4"
                data-testid={`mc-vs-${data.slug}-strength-${i}`}
              >
                <CheckCircle
                  size={18}
                  weight="fill"
                  className="text-emerald-400 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-white/80 leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* GAPS / WHERE THE COST SHOWS UP — outcome only */}
      <section className="px-6 py-14 border-b border-white/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black mb-2">
            {data.gapHeadline}
          </h2>
          <p className="text-sm text-white/55 mb-8">
            None of these are bugs — they&apos;re design choices. {data.name}{' '}
            optimizes for a different shape of customer. If your data looks
            like the items below, that gap matters.
          </p>
          <ul className="space-y-3">
            {data.gaps.map((g, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-amber-500/[0.04] border border-amber-500/20 rounded-md p-4"
                data-testid={`mc-vs-${data.slug}-gap-${i}`}
              >
                <WarningCircle
                  size={18}
                  weight="fill"
                  className="text-amber-300 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-white/85 leading-relaxed">{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TIER-BY-TIER COMPARISON */}
      <section className="px-6 py-16 border-b border-white/10 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black mb-3">
            Tier-by-tier: where the price actually breaks
          </h2>
          <p className="text-sm text-white/60 mb-10 max-w-3xl">
            Five rows. Every MigrationCrunch tier alongside the closest{' '}
            {data.name} equivalent at the same data volume. Pricing reflects
            publicly published rates as of February 2026.
          </p>

          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table
              className="w-full text-sm"
              data-testid={`mc-vs-${data.slug}-tier-table`}
            >
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left p-3 font-bold text-amber-300 text-xs uppercase tracking-wider w-1/4">
                    MigrationCrunch
                  </th>
                  <th className="text-left p-3 font-bold text-white/60 text-xs uppercase tracking-wider w-1/4">
                    {data.name}
                  </th>
                  <th className="text-left p-3 font-bold text-emerald-300 text-xs uppercase tracking-wider w-2/4">
                    What changes for you
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.tierMap.map((row, i) => (
                  <tr
                    key={i}
                    className="border-t border-white/5 align-top hover:bg-white/[0.02]"
                    data-testid={`mc-vs-${data.slug}-tier-row-${i}`}
                  >
                    <td className="p-3">
                      <div className="font-black text-amber-300">
                        {row.ours.name}
                      </div>
                      <div className="font-mono text-xs text-white/80 mt-1">
                        {row.ours.price}
                      </div>
                      <div className="text-[11px] text-white/45 mt-0.5">
                        {row.ours.range}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-bold text-white/85">
                        {row.theirs.name}
                      </div>
                      <div className="font-mono text-xs text-white/70 mt-1">
                        {row.theirs.price}
                      </div>
                      <div className="text-[11px] text-white/45 mt-0.5">
                        {row.theirs.range}
                      </div>
                    </td>
                    <td className="p-3 text-xs text-white/80 leading-relaxed">
                      {row.edge}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[11px] text-white/35 mt-3">
            Sources: {data.domain} (public pricing pages and calculators) and
            our own evaluations. Tiers are matched by data volume; the exact
            named SKU on each side may differ.
          </p>
        </div>
      </section>

      {/* WHEN TO PICK THEM */}
      <section className="px-6 py-14 border-b border-white/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black mb-3">
            When you should pick {data.name} instead
          </h2>
          <p className="text-sm text-white/60 mb-8">
            We&apos;d rather lose the sale than the trust. If any of these match
            your situation, stop reading and go talk to them.
          </p>
          <ul className="space-y-3">
            {data.pickThemWhen.map((p, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-md p-4"
                data-testid={`mc-vs-${data.slug}-pickthem-${i}`}
              >
                <Sparkle
                  size={16}
                  weight="fill"
                  className="text-white/60 mt-1 flex-shrink-0"
                />
                <span className="text-sm text-white/85 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQs */}
      <section className="px-6 py-16 border-b border-white/10 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black mb-8">
            Common questions, answered straight
          </h2>
          <div className="space-y-4">
            {data.faqs.map((f, i) => (
              <details
                key={i}
                className="group bg-white/[0.03] border border-white/10 rounded-md p-4"
                data-testid={`mc-vs-${data.slug}-faq-${i}`}
              >
                <summary className="cursor-pointer font-bold text-white text-sm flex items-start gap-2 list-none">
                  <span className="text-amber-300">Q.</span>
                  <span className="flex-1">{f.q}</span>
                  <span className="text-white/40 text-xs group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-white/75 leading-relaxed pl-5">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM LINE — Lindy's honest one-paragraph verdict. Runs right
          before the CTA so the last thing readers absorb is the clean
          "here's who each tool is for" honest framing. */}
      {data.bottomLine && (
        <section className="px-6 py-14 border-b border-white/10 bg-white/[0.03]" data-testid={`mc-vs-${data.slug}-bottom-line`}>
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-xs font-black uppercase tracking-widest text-amber-400 mb-3">
              🎯 The bottom line
            </div>
            <p className="text-base md:text-lg text-white/85 leading-relaxed">
              {data.bottomLine}
            </p>
          </div>
        </section>
      )}

      {/* CROSS-LINKS to other competitor pages — SEO interlink graph */}
      <section className="px-6 py-14 border-b border-white/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl md:text-2xl font-black mb-2">
            Comparing other tools too?
          </h2>
          <p className="text-sm text-white/55 mb-6">
            We keep these pages up to date as competitor pricing changes. Pick
            another head-to-head:
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {otherCompetitors.map((slug) => {
              const c = getCompetitor(slug);
              return (
                <Link
                  key={slug}
                  to={`/migrate/vs/${slug}`}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-400/40 rounded-md p-4 transition-colors"
                  data-testid={`mc-vs-${data.slug}-xlink-${slug}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
                    vs
                  </div>
                  <div className="font-bold text-white text-sm">{c.name}</div>
                  <div className="text-[11px] text-white/50 mt-1 line-clamp-2">
                    {c.tagline}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            Want to see the difference on your actual data?
          </h2>
          <p className="text-white/70 mb-8 text-base md:text-lg">
            Upload up to 50 contacts + notes from your old CRM. Free. Real export.
            You&apos;ll see exactly what gets pulled out of your notes — before you
            commit a dollar to anyone.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/migrate#fit-check"
              className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-md transition-colors inline-flex items-center justify-center gap-2"
              data-testid={`mc-vs-${data.slug}-bottom-fit-cta`}
            >
              <Sparkle size={16} weight="fill" />
              Take the fit check
            </a>
            <Link
              to="/migrate"
              className="px-6 py-3 border border-white/20 hover:border-white/40 text-white font-medium rounded-md transition-colors inline-flex items-center justify-center gap-2"
              data-testid={`mc-vs-${data.slug}-bottom-home-cta`}
            >
              See how MigrationCrunch works
            </Link>
          </div>
          <p className="text-xs text-white/40 mt-6">
            30-day money-back guarantee on AI note recovery. One-time pricing.
            No subscriptions.
          </p>
        </div>
      </section>

      <MigrationCrunchFooter />

      {/* Migration Crunchy — the /vs pages are migration-intent traffic */}
      <MigrationConciergeFloat />
    </div>
  );
}
